// Real backend integration for the RCT Experiment feature. Mirrors the
// manual Synthetic Control (GeoLift) pattern: presigned S3 upload, generic
// /api/jobConfig + /api/jobRun endpoints (no bespoke RCT creation endpoint
// needed - RCT_EXPERIMENT is just another JobType those already handle),
// and the generic /api/{model_type}/output/{jobConfigId} endpoint for
// results. The one new endpoint is GET /api/jobRun/{run_id}/progress, added
// specifically for this feature's long-running (tens of minutes - market
// pairing and the full trial batch re-run per candidate duration within
// each split, ~20 min validated for one split across 3 durations) design job.

import Papa from "papaparse";
import * as XLSX from "xlsx";
import axiosInstance from "../../../utils/axiosInstance";
import { uploadFileToS3 } from "../../../utils/s3Upload";
import { JobType } from "../../../constants/JobTypes";
import { SetupState, SPLIT_PERCENTAGES } from "./mockData";
import { detectDateColumn, detectLocationColumn, detectKpiColumn } from "../../../utils/dataValidation";

// ---------------------------------------------------------------------------
// File parsing + auto-detection (CSV or Excel)
// ---------------------------------------------------------------------------

// The data scientist's original script never assumed a raw file already had
// literal "date"/"location"/"Y" headers - generateOutputRCTGeoLiftWeekWise()
// took arbitrary source column names as parameters (dateColumn, locationColumn,
// KPIColumn). Rather than making the user map columns by hand, this samples
// a handful of data rows and classifies each column by what its *values*
// look like (parses as a date? numeric? plain text?) - no user step, no
// requirement on header names. detectDateColumn/detectLocationColumn/
// detectKpiColumn (from dataValidation.ts, already used by GeoLift) are
// reused as header-name hints to break ties between equally-valid candidates.
//
// That same source script also aggregated daily rows into weekly buckets
// before handing off to the design functions (computeBlossomsBasedPair,
// generateAggregateOLS, etc. all key off one row = one location-week). The
// ported rct_experiment.py has no aggregation step of its own - it assumes
// the input is already weekly - so aggregateToWeeklyRows() below replicates
// generateOutputRCTGeoLiftWeekWise()'s bucketing client-side: every
// (location, calendar-week-since-min-date) pair gets one row, KPI summed
// into it, zero-filled if that location had no rows that week.

export function isExcelFile(file: File): boolean {
  return /\.xlsx?$/i.test(file.name);
}

export function parseCsvRows(file: File): Promise<{ headers: string[]; rows: Record<string, string>[] }> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) =>
        resolve({ headers: result.meta.fields || [], rows: result.data as Record<string, string>[] }),
      error: (err: Error) => reject(err),
    });
  });
}

// Days between the Excel 1900-date-system epoch and the Unix epoch - the
// standard constant for converting an Excel date serial to a JS timestamp.
const EXCEL_EPOCH_OFFSET_DAYS = 25569;

// SheetJS's own `cellDates` conversion builds a Date from local-time
// components internally, so a plain date-only cell can shift by a day
// depending on the browser's timezone (verified: it turned an Excel cell
// displaying "7/1/25" into 2025-06-30T18:29:50Z on a UTC+5:30 machine).
// Excel's underlying serial number is timezone-agnostic - converting it
// ourselves with the well-known epoch-offset formula always reproduces
// exactly the calendar date Excel itself displays.
const excelSerialToIsoDate = (serial: number): string =>
  new Date(Math.round((serial - EXCEL_EPOCH_OFFSET_DAYS) * 86400 * 1000)).toISOString().slice(0, 10);

export async function parseExcelRows(file: File): Promise<{ headers: string[]; rows: Record<string, string>[] }> {
  const buffer = await file.arrayBuffer();
  // cellNF keeps each cell's number-format code around so real date cells
  // (formatted as a date in the source spreadsheet) can be told apart from
  // plain numeric columns like a DMA code or a sales count - see below.
  const workbook = XLSX.read(buffer, { type: "array", cellNF: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
  const headers = rawRows.length > 0 ? Object.keys(rawRows[0]) : [];

  // Ask Excel's own formatting - not value-sniffing - which columns are
  // dates: any column with at least one data cell whose number format is a
  // recognized date format. Keyed by header name (not column index) so it's
  // robust to sparse/empty leading cells. Also keep each date column's
  // index - see below.
  const dateColumnNames = new Set<string>();
  const dateColumnIndex = new Map<string, number>();
  const range = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null;
  if (range) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerCell = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: col })];
      const headerName = headerCell ? String(headerCell.v) : null;
      if (!headerName || dateColumnNames.has(headerName)) continue;
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && cell.t === "n" && cell.z && XLSX.SSF.is_date(cell.z)) {
          dateColumnNames.add(headerName);
          dateColumnIndex.set(headerName, col);
          break;
        }
      }
    }
  }

  const rows = rawRows.map((row) => {
    const out: Record<string, string> = {};
    // sheet_to_json unconditionally converts any cell whose number format
    // is a recognized date format into a JS Date object - true regardless
    // of the `raw` option (see SheetJS's make_json_row: it forces
    // numdate() for any 'n'-typed cell with a date-like z format before
    // `raw` is ever consulted). That Date is exactly the timezone-lossy
    // conversion excelSerialToIsoDate exists to avoid, so for date columns
    // sheet_to_json's value is ignored entirely and the untouched Excel
    // serial number is re-read straight from the sheet, using the hidden
    // __rowNum__ every sheet_to_json row carries (non-enumerable, so it
    // doesn't show up in Object.entries/headers above).
    const rowNum = (row as { __rowNum__?: number }).__rowNum__;
    Object.entries(row).forEach(([key, value]) => {
      if (dateColumnNames.has(key) && rowNum !== undefined) {
        const col = dateColumnIndex.get(key)!;
        const rawCell = sheet[XLSX.utils.encode_cell({ r: rowNum, c: col })];
        out[key] = typeof rawCell?.v === "number" ? excelSerialToIsoDate(rawCell.v) : "";
      } else {
        out[key] = value === null || value === undefined ? "" : String(value);
      }
    });
    return out;
  });
  return { headers, rows };
}

export async function parseFileRows(
  file: File
): Promise<{ headers: string[]; rows: Record<string, string>[] }> {
  return isExcelFile(file) ? parseExcelRows(file) : parseCsvRows(file);
}

export interface RctFileValidation {
  valid: boolean;
  reason?: string;
  dateColumn?: string;
  locationColumn?: string;
  kpiColumn?: string;
}

const SAMPLE_SIZE = 5; // non-empty values per column, not a shared row window - see scoreColumnsByValue
const CONFIDENCE_THRESHOLD = 0.8; // fraction of a column's sampled values that must match to qualify

// JS's `Date` constructor is far too permissive on its own - bare numeric
// strings like "845" or "0" parse as valid dates (year 845, year 1999 via
// epoch-ms-as-year quirks), which would misclassify plain KPI counts as
// dates before they ever get scored as numeric. Require the value to
// structurally look like a date (separators, or a month name, or an
// unambiguous 8-digit YYYYMMDD) before trusting Date.parse at all.
const DATE_SHAPE_PATTERN =
  /^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}$|^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$|^\d{8}$|^[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{4}$|^\d{1,2}\s+[A-Za-z]{3,9}\.?,?\s+\d{4}$/;

const isDateLikeValue = (value: string): boolean => {
  const v = value.trim();
  if (!v || !DATE_SHAPE_PATTERN.test(v)) return false;
  return !isNaN(new Date(v).getTime());
};

const stripNumericFormatting = (value: string): { cleaned: string; negative: boolean } => {
  let v = value.trim();
  // Accounting-style negative, e.g. "(1,234.56)"
  const negative = /^\(.*\)$/.test(v);
  if (negative) v = v.slice(1, -1);
  // $, %, commas, and spaces (thousands separator in some exports)
  return { cleaned: v.replace(/[$,%\s]/g, ""), negative };
};

const isNumericLikeValue = (value: string): boolean => {
  const { cleaned } = stripNumericFormatting(value);
  return cleaned !== "" && /^-?\d+(\.\d+)?$/.test(cleaned);
};

// Classifies each column as date-like / numeric-like / plain-text by
// sampling its first few *non-empty* values - independently per column,
// scanning down the file until it finds SAMPLE_SIZE of them (or runs out of
// rows). This matters because a shared fixed row-window (e.g. just the
// first 5 rows of the whole file) can make a genuinely numeric column score
// 0 for no reason other than being sparse/blank/sorted-to-zero in those
// particular rows - a column shouldn't be penalized for what OTHER columns
// look like in the same rows.
function scoreColumnsByValue(headers: string[], rows: Record<string, string>[]) {
  const scores: Record<string, { date: number; numeric: number; text: number }> = {};
  headers.forEach((h) => {
    let dateHits = 0;
    let numericHits = 0;
    let textHits = 0;
    let total = 0;
    for (const row of rows) {
      if (total >= SAMPLE_SIZE) break;
      const v = (row[h] ?? "").toString().trim();
      if (!v) continue;
      total++;
      if (isDateLikeValue(v)) dateHits++;
      else if (isNumericLikeValue(v)) numericHits++;
      else textHits++;
    }
    scores[h] = {
      date: total ? dateHits / total : 0,
      numeric: total ? numericHits / total : 0,
      text: total ? textHits / total : 0,
    };
  });
  return scores;
}

// Auto-detects date/location/KPI columns from actual row values (no user
// interaction) and reports whether the file "looks correct" or not. Value
// scoring is primary; a header-name hint (detectDateColumn etc., from
// dataValidation.ts) is used to break ties between multiple columns that
// clear the threshold, and as a fallback - if nothing clears the strict
// threshold but a hint column at least has *some* matching sampled values,
// trust the name rather than hard-failing on a sparse/ambiguous sample.
export function validateRctFileRows(
  headers: string[],
  rows: Record<string, string>[]
): RctFileValidation {
  if (headers.length === 0 || rows.length === 0) {
    return { valid: false, reason: "The file appears to be empty." };
  }

  const scores = scoreColumnsByValue(headers, rows);

  const dateHint = detectDateColumn(headers).column;
  let dateCandidates = headers.filter((h) => scores[h].date >= CONFIDENCE_THRESHOLD);
  if (dateCandidates.length === 0 && dateHint && scores[dateHint].date > 0) {
    dateCandidates = [dateHint];
  }
  if (dateCandidates.length === 0) {
    return {
      valid: false,
      reason: `Couldn't find a column where the sampled rows look like dates (checked: ${headers.join(", ")}).`,
    };
  }
  const dateColumn = dateHint && dateCandidates.includes(dateHint) ? dateHint : dateCandidates[0];

  const remainingAfterDate = headers.filter((h) => h !== dateColumn);
  const locationHint = detectLocationColumn(headers).column;
  let locationCandidates = remainingAfterDate.filter((h) => scores[h].text >= CONFIDENCE_THRESHOLD);
  if (
    locationCandidates.length === 0 &&
    locationHint &&
    remainingAfterDate.includes(locationHint) &&
    scores[locationHint].text > 0
  ) {
    locationCandidates = [locationHint];
  }
  if (locationCandidates.length === 0) {
    return {
      valid: false,
      reason: "Couldn't find a text column (e.g. a market/DMA name) other than the date column.",
    };
  }
  const locationColumn =
    locationHint && locationCandidates.includes(locationHint) ? locationHint : locationCandidates[0];

  const remainingAfterLocation = remainingAfterDate.filter((h) => h !== locationColumn);
  const kpiHint = detectKpiColumn(headers, [dateColumn, locationColumn]).column;
  let kpiCandidates = remainingAfterLocation.filter((h) => scores[h].numeric >= CONFIDENCE_THRESHOLD);
  if (
    kpiCandidates.length === 0 &&
    kpiHint &&
    remainingAfterLocation.includes(kpiHint) &&
    scores[kpiHint].numeric > 0
  ) {
    kpiCandidates = [kpiHint];
  }
  if (kpiCandidates.length === 0) {
    return {
      valid: false,
      reason: "Couldn't find a numeric KPI column (e.g. sales or conversions) besides date and location.",
    };
  }
  const kpiColumn = kpiHint && kpiCandidates.includes(kpiHint) ? kpiHint : kpiCandidates[0];

  return { valid: true, dateColumn, locationColumn, kpiColumn };
}

const parseNumeric = (raw: string | undefined): number => {
  if (!raw) return 0;
  const { cleaned, negative } = stripNumericFormatting(raw);
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return 0;
  return negative ? -Math.abs(n) : n;
};

// Mirrors generateOutputRCTGeoLiftWeekWise(): groups by (location, week-since
// -min-date), summing the KPI column into each bucket, with every location
// getting a row for every week (zero-filled) - a straight cartesian product
// of locations x weeks, same as the original script.
export function aggregateToWeeklyRows(
  rows: Record<string, string>[],
  dateColumn: string,
  locationColumn: string,
  kpiColumn: string
): { date: string; location: string; Y: number }[] {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  const daily = rows
    .map((row) => ({
      date: new Date(row[dateColumn]),
      location: (row[locationColumn] || "").trim(),
      Y: parseNumeric(row[kpiColumn]),
    }))
    .filter((r) => r.location && !isNaN(r.date.getTime()));

  if (daily.length === 0) return [];

  // Not Math.min(...daily.map(...)) / Math.max(...) - spreading a large
  // array as call arguments blows V8's call-stack argument limit (~100k-125k
  // elements). Harmless for the standalone RCT Experiment upload (already
  // pre-aggregated to one row per location-week, so rarely huge), but Causal
  // Loop's shared file still has every Bid Optimizer segment row (age x
  // gender x device x location x date) before this function aggregates it -
  // real reference data hit 147,576 rows, well past the crash threshold.
  let minDateMs = Infinity;
  let maxDateMs = -Infinity;
  for (const r of daily) {
    const ms = r.date.getTime();
    if (ms < minDateMs) minDateMs = ms;
    if (ms > maxDateMs) maxDateMs = ms;
  }
  const numberOfDays = Math.round((maxDateMs - minDateMs) / MS_PER_DAY);
  const numberOfWeeks =
    numberOfDays % 7 === 0 ? numberOfDays / 7 : Math.floor(numberOfDays / 7) + 1;
  const weekCount = Math.max(1, numberOfWeeks);

  const weekStartMs: number[] = [];
  for (let i = 0; i < weekCount; i++) {
    weekStartMs.push(minDateMs + i * 7 * MS_PER_DAY);
  }

  // Any date beyond the last bucket's nominal start also falls in the last
  // bucket - matches the original script's "else" branch for the final week.
  const weekIndexOf = (ms: number) => {
    const idx = Math.floor((ms - minDateMs) / (7 * MS_PER_DAY));
    return Math.min(Math.max(idx, 0), weekCount - 1);
  };

  const bucketSums = new Map<string, number>();
  daily.forEach((r) => {
    const key = `${r.location}__${weekIndexOf(r.date.getTime())}`;
    bucketSums.set(key, (bucketSums.get(key) || 0) + r.Y);
  });

  const uniqueLocations = Array.from(new Set(daily.map((r) => r.location))).sort();

  const result: { date: string; location: string; Y: number }[] = [];
  uniqueLocations.forEach((location) => {
    weekStartMs.forEach((startMs, idx) => {
      result.push({
        date: new Date(startMs).toISOString().slice(0, 10),
        location,
        Y: bucketSums.get(`${location}__${idx}`) || 0,
      });
    });
  });

  return result;
}

// Serializes the aggregated canonical rows into the CSV the backend expects
// at input/rct_experiment_data.csv - this is the only file format/shape the
// design engine (rct_experiment.py, pd.read_csv) ever sees.
export function buildRctInputFile(
  weeklyRows: { date: string; location: string; Y: number }[],
  originalFileName: string
): File {
  const csv = Papa.unparse(weeklyRows);
  const csvName = originalFileName.replace(/\.[^.]+$/, "") + ".csv";
  return new File([csv], csvName, { type: "text/csv" });
}

// ---------------------------------------------------------------------------
// Design result types (shape of rct_experiment.py's runRctExperimentDesign output)
// ---------------------------------------------------------------------------

export interface SplitPreviewEntry {
  testMarkets: number;
  controlMarkets: number;
  avgMatchDistance: number;
  weeklyCost: number;
  treatmentLocations: string[];
  controlLocations: string[];
}

export interface DesignResultRow {
  splitKey: string;
  duration: number;
  testMarkets: number;
  controlMarkets: number;
  weeklyCost: number;
  totalInvestment: number;
  mde: number;
  percentageLift: number;
  incrementalInvestment: number;
  validationRMSPE: number;
  validationSD: number;
  validationBias: number;
  averageBaselineKPI: number;
  standardError: number;
  compositeZScore: number;
  // The winning trial's own error score, normalized against every trial
  // actually tried for this split (thousands of candidates) - unlike
  // compositeZScore above, which normalizes across different splits and is
  // always 0 today since only one split can be requested per job. Lower is
  // better; meaningful on its own.
  normalizedAverageError: number;
  // Assumed post-campaign lift scenario (e.g. 1.1 = actual KPI modeled at
  // 2.1x baseline) that MDE/percentageLift/incrementalInvestment above were
  // computed at - calibrated server-side so the implied weekly investment
  // matches the Setup screen's Weekly Investment Cap. 0 when no cap was set.
  selectedEffectSize: number;
  // Week-by-week actual vs. predicted test-market average over the
  // post-campaign window (same length/order across all three arrays).
  actualDates: string[];
  actualKPI: number[];
  predictedKPI: number[];
}

export interface DesignResult {
  droppedLocations: string[];
  fixedTreatmentLocations: string[];
  fixedControlLocations: string[];
  splitPreview: Record<string, SplitPreviewEntry>;
  results: DesignResultRow[];
  // Full-history total KPI per location (every uploaded location) - keyed
  // by location name, e.g. {"new york": 84521.3, ...}.
  locationTotalSales: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export type JobPhase = "validating" | "pairing" | "simulating" | "scoring" | "completed" | "failed";

export interface JobRunProgress {
  phase: JobPhase | string;
  progressPct: number;
  message?: string;
}

const PHASE_LABELS: Record<string, string> = {
  validating: "Screening locations",
  pairing: "Matching test & control markets",
  simulating: "Sizing power & investment",
  scoring: "Ranking split scenarios",
  completed: "Design complete",
  failed: "Design failed",
};

export const describePhase = (phase?: string): string =>
  (phase && PHASE_LABELS[phase]) || phase || "Queued";

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function createRctExperimentJob(
  jobConfigId: string,
  setup: SetupState
): Promise<{ jobRunId: string }> {
  if (!setup.file) {
    throw new Error("No file selected");
  }

  await uploadFileToS3(setup.file, `${jobConfigId}/input/rct_experiment_data.csv`);
  // Archival copy of exactly what the user selected, before client-side
  // weekly aggregation - keeps input/ showing both the raw upload and the
  // derived file the algorithm actually reads. Fire-and-forget, same
  // convention as uploadMarketLevelWorkbook below: doesn't block or fail
  // job creation if the archival copy fails.
  uploadFileToS3(setup.file, `${jobConfigId}/input/raw_${setup.file.name}`).catch((err) => {
    console.error("[RctExperiment] Failed to archive raw upload to S3:", err);
  });

  const splits: Record<string, number> = {};
  setup.splits.forEach((key) => {
    splits[key] = SPLIT_PERCENTAGES[key];
  });

  await axiosInstance.post("/api/jobConfig", {
    jobConfigId,
    name: setup.studyName,
    jobType: JobType.RctExperiment,
    dataSourceConfig: { dataSourceType: "FILE_CSV" },
    modelVersion: "V1",
    modelParameterConfig: {
      parameters: {
        durations: setup.durations,
        splits,
        totalWeeklySpend: setup.totalWeeklySpend || 0,
        weeklyInvestmentCap: setup.weeklyCap || 0,
      },
    },
  });

  const runResponse = await axiosInstance.post("/api/jobRun", { jobConfigId });

  return { jobRunId: runResponse.data?.jobRun?.jobRunId };
}

export async function fetchJobRunProgress(jobRunId: string): Promise<JobRunProgress | null> {
  const response = await axiosInstance.get(`/api/jobRun/${jobRunId}/progress`, {
    validateStatus: (status: number) => status === 200 || status === 204,
  });
  if (response.status === 204 || !response.data?.result) return null;
  const result = response.data.result;
  return {
    phase: result.phase,
    progressPct: Number(result.progressPct ?? 0),
    message: result.message,
  };
}

// Generic job-run status (PENDING/RUNNING/SUCCESSFUL/FAILED). Needed
// alongside progress because a handful of early-exit failures in the SQS
// job processor (bad input file, missing parameters) never write a
// progress row at all - status is the only terminal signal for those.
export async function fetchJobRunStatus(jobRunId: string): Promise<string | null> {
  const response = await axiosInstance.get(`/api/jobRun/${jobRunId}`);
  return response.data?.jobRun?.jobStatus ?? null;
}

export async function fetchDesignResult(jobConfigId: string): Promise<DesignResult | null> {
  const response = await axiosInstance.get(`/api/rct-experiment/output/${jobConfigId}`, {
    validateStatus: (status: number) => status === 200 || status === 204,
  });
  if (response.status === 204 || !response.data?.result) return null;
  return (response.data.result.design_result as DesignResult) ?? null;
}

export interface RctExperimentSummary {
  jobConfigId: string;
  name: string;
  status?: string;
  createdDate: string;
}

export async function fetchRctExperiments(): Promise<RctExperimentSummary[]> {
  const response = await axiosInstance.get(`/api/jobConfig/jobType/${JobType.RctExperiment}`, {
    params: { limit: 100, offset: 0 },
  });
  const items = response.data?.items ?? [];
  return items.map((item: any) => ({
    jobConfigId: item.jobConfigId,
    name: item.name,
    status: item.status,
    createdDate: item.createdDate,
  }));
}

// The listing page's real status per experiment - JobConfig.status is a
// separate, largely-unused field nothing ever sets for this feature (unlike
// GeoLift's "recommendation_success" convention). The actual PENDING/
// RUNNING/SUCCESSFUL/FAILED state lives on JobRun, same as every other job
// type, so it has to be fetched and matched by jobConfigId - mirrors
// GeoLift's fetchJobRunList + getJobStatus pattern exactly.
export interface RctExperimentRun {
  jobConfigId: string;
  jobStatus: string;
  jobExecutionTime: string;
}

export async function fetchRctExperimentRuns(): Promise<RctExperimentRun[]> {
  const response = await axiosInstance.get(`/api/jobRun/jobType/${JobType.RctExperiment}`, {
    params: { limit: 100, offset: 0 },
  });
  return response.data?.items ?? [];
}

export async function deleteRctExperiment(jobConfigId: string): Promise<void> {
  await axiosInstance.delete(`/api/jobConfig/${jobConfigId}`);
}

export interface RctExperimentConfig {
  name: string;
  durations: number[];
  splits: string[];
  totalWeeklySpend: number | null;
  weeklyCap: number | null;
}

export async function fetchRctExperimentConfig(jobConfigId: string): Promise<RctExperimentConfig | null> {
  const response = await axiosInstance.get(`/api/jobConfig/${jobConfigId}`);
  const config = response.data;
  if (!config) return null;
  const parameters = config.modelParameterConfig?.parameters ?? {};
  return {
    name: config.name,
    durations: parameters.durations ?? [],
    splits: Object.keys(parameters.splits ?? {}),
    totalWeeklySpend: parameters.totalWeeklySpend ?? null,
    weeklyCap: parameters.weeklyInvestmentCap ?? null,
  };
}

// ---------------------------------------------------------------------------
// Market Level Investments export (.xlsx)
// ---------------------------------------------------------------------------

export interface MarketLevelExportRow {
  name: string;
  type: string;
  totalSalesCount: number;
  percentageOfTreatment: number;
}

// Builds an .xlsx workbook client-side (SheetJS) from the Market Level
// Investments table - that data is fully derivable from splitPreview +
// locationTotalSales, already loaded in both the live run and view mode, so
// there's no need to round-trip through the backend to produce the file.
export function buildMarketLevelWorkbookBlob(rows: MarketLevelExportRow[], sheetName: string): Blob {
  const sheetRows = rows.map((r) => ({
    Market: r.name,
    Type: r.type,
    "Total Sales Count": r.totalSalesCount,
    "Percentage Of Treatment": r.percentageOfTreatment,
  }));
  const worksheet = XLSX.utils.json_to_sheet(sheetRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31)); // Excel sheet names cap at 31 chars
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Archival copy in S3, grouped per split (tenant/jobConfigId/output/{splitKey}/...
// - same output/{splitKey}/... convention buildOutputArtifacts uses server-side,
// so every artifact for a given split lives together) - not on the download's
// critical path, so callers should fire this without awaiting/blocking the
// user-facing download.
export async function uploadMarketLevelWorkbook(
  jobConfigId: string,
  splitKey: string,
  blob: Blob
): Promise<void> {
  const filename = "market_level_investments.xlsx";
  const file = new File([blob], filename, { type: blob.type });
  await uploadFileToS3(file, `${jobConfigId}/output/${splitKey}/${filename}`);
}
